import React from "react";
import Select, {StylesConfig} from "react-select";
import {UseFormRegister, FieldValues, Path} from 'react-hook-form';
import styles from "./Input.module.scss";
import cn from "classnames";

interface Option {
    value: string;
    label: string;
    id: string;
}

interface SelectSearchProps<T extends FieldValues> {
    label?: string;
    name: Path<T>;
    register: UseFormRegister<T>;
    className?: string;
    errorMessage?: string;
    options: Option[];
    defaultValue?: Option | null;
    setValue: (name: Path<T>, value: any) => void;
}

const SelectSearch = <T extends FieldValues>(
    {
        label,
        name,
        register,
        className,
        errorMessage,
        options,
        defaultValue,
        setValue,
    }: SelectSearchProps<T>) => {

    const customStyles: StylesConfig<Option, false> = {
        control: (provided, state) => ({
            ...provided,
            background: '#EDEEF0',
            borderColor: errorMessage ? '#FF5959' : '#A9A9AB',
            borderRadius: '8px',
            padding: '7px 6px',
            fontWeight: 300,
            fontSize: '16px',
            lineHeight: '26px',
            color: '#000000',
            cursor: 'pointer',
            boxShadow: state.isFocused ? 'none' : 'none',
            '&:focus': {
                borderColor: '#27272A',
                boxShadow: 'none',
            },
            '&:hover': {
                borderColor: state.isFocused ? '#27272A' : '#A9A9AB',
            },
            '@media (min-width: 768px)': {
                borderRadius: '12px',
                padding: '10px 14px',
                fontSize: '18px',
                lineHeight: '28px',
            },
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 100,
            borderRadius: '12px',
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '0',
            borderRadius: '12px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#FF5959' : state.isFocused ? '#f0f0f0' : 'white',
            color: state.isSelected ? 'white' : 'black',
            cursor: 'pointer',
        }),
    };

    const sortedOptions = React.useMemo(() => {
        return options
            .filter(option => option.label !== 'Иное') // Убираем "Иное"
            .sort((a, b) => {
                if (a.label === 'Москва') return -1; // Москва всегда первая
                if (b.label === 'Москва') return 1;
                if (a.label === 'Санкт-Петербург') return -1; // Санкт-Петербург всегда вторая
                if (b.label === 'Санкт-Петербург') return 1;
                return a.label.localeCompare(b.label); // Сортируем остальные по алфавиту
            });
    }, [options]);

    const handleChange = (selectedOption: Option | null) => {
        if (selectedOption) {
            const {value, id} = selectedOption;
            register(name).onChange({
                target: {
                    value,
                },
            });
            setValue(name, value);
            setValue("countryId" as Path<T>, id); // Приводим к типу Path<T>
        } else {
            setValue(name, "");
            setValue("countryId" as Path<T>, ""); // Очищаем countryId
        }
    };

    return (
        <div className={cn(styles.inputGroup, className)}>
            {label && <label className={styles.inputLabel} htmlFor={name}>{label}</label>}
            <Select
                options={sortedOptions}
                onChange={handleChange}
                value={defaultValue}
                styles={customStyles}
                isClearable
                placeholder="Выберите регион"
                noOptionsMessage={() => "Нет доступных вариантов"}
            />
            {errorMessage && <span className={styles.errorMessage}>{errorMessage}</span>}
        </div>
    );
};

export default SelectSearch;
